"use client";

import type { VariantProps } from "class-variance-authority";
import type { NavBarAppearance, NavigationMenuData } from "../../../types";

import { trackMobileMenuToggleTrackedClickEvent } from "@homeinstead/analytics";
import { cva } from "class-variance-authority";
import { useState } from "react";
import { cn } from "../../../utils/classnames";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "../../Drawer";
import HamburgerMenu from "../../HamburgerMenu";
import { NavigationMenu } from "./NavigationMenu";

const mobileDrawerContentVariants = cva(
  "inset-x-0 z-10 bg-support-tints-400 font-jakarta",
  {
    defaultVariants: {
      topOffset: 32,
    },
    variants: {
      topOffset: {
        0: "top-0",
        16: "top-16",
        20: "top-20",
        24: "top-24",
        28: "top-28",
        32: "top-32",
      },
    },
  },
);

interface MobileDrawerProps
  extends VariantProps<typeof mobileDrawerContentVariants> {
  appearance?: NavBarAppearance;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  menuItems: NavigationMenuData[];
  className?: string;
}

export interface MobileNavigationMenuProps
  extends Pick<MobileDrawerProps, "appearance" | "topOffset"> {
  menuItems: NavigationMenuData[];
}

const MobileDrawer = ({
  appearance = "default",
  isOpen,
  onOpenChange,
  menuItems,
  className,
  topOffset,
}: MobileDrawerProps) => (
  <div className={className}>
    <Drawer direction="bottom" onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <HamburgerMenu
          className="inline-flex cursor-pointer text-support-base group-data-[appearance=campaign]/navbar:text-new-beginnings-base"
          isOpen={isOpen}
          menuSpanClassName="bg-white group-data-[appearance=campaign]/navbar:bg-new-beginnings-base"
        />
      </DrawerTrigger>

      {/* Mirror nav's group/navbar + data-appearance so drawer menu items share
          the same appearance selectors (drawer portals outside `<nav>`). */}
      <DrawerContent
        className={cn(
          "group/navbar",
          mobileDrawerContentVariants({ topOffset }),
        )}
        data-appearance={appearance}
      >
        <DrawerTitle className="sr-only">Navigation Menu</DrawerTitle>
        <div className="relative p-14 px-6 md:px-12">
          <div
            aria-label="mobile pull tab"
            className="absolute top-4 left-1/2 h-1 w-14 -translate-x-1/2 rounded-full border-1 bg-new-beginnings-base"
          ></div>
          <NavigationMenu asChild className="w-full" menuItems={menuItems} />
        </div>
      </DrawerContent>
    </Drawer>
  </div>
);

export const MobileNavigationMenu = ({
  appearance = "default",
  menuItems,
  topOffset,
}: MobileNavigationMenuProps) => {
  const [isMenuDrawerOpen, setMenuIsDrawerOpen] = useState<boolean>(false);

  return (
    <div className="flex w-full flex-row items-center justify-end gap-4">
      <MobileDrawer
        appearance={appearance}
        className="transition-all duration-300 ease-in-out"
        isOpen={isMenuDrawerOpen}
        menuItems={menuItems}
        onOpenChange={(isOpen) => {
          trackMobileMenuToggleTrackedClickEvent({
            is_open: isOpen,
          });
          setMenuIsDrawerOpen(isOpen);
        }}
        topOffset={topOffset}
      />
    </div>
  );
};
